import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import QRCode from 'qrcode';
import { CreditCard, X, Star } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';
const SITE = 'https://www.eco-enr.fr';

const CarteProfessionnelle = () => {
  const [carte, setCarte] = useState(null);
  const [ouvert, setOuvert] = useState(false);
  const [qr, setQr] = useState(null);
  const refCarte = useRef(null);
  const refReflet = useRef(null);

  const charger = async () => {
    try {
      const r = await axios.get(`${API}/api/employes/ma-carte`);
      setCarte(r.data?.data || null);
    } catch (e) {}
  };

  useEffect(() => { charger(); }, []);

  useEffect(() => {
    QRCode.toDataURL(SITE, { margin: 0, width: 200, color: { dark: '#0B6B4F', light: '#FFFFFF' } })
      .then(setQr)
      .catch(() => {});
  }, []);

  const ouvrir = async () => {
    await charger();
    setOuvert(true);
  };

  useEffect(() => {
    if (!ouvert || !refCarte.current) return;
    refCarte.current.animate([
      { transform: 'translateY(150%) rotate(-8deg) scale(0.9)', opacity: 0 },
      { transform: 'translateY(-8%) rotate(2deg) scale(1.02)', opacity: 1, offset: 0.65 },
      { transform: 'translateY(0) rotate(0deg) scale(1)', opacity: 1 }
    ], { duration: 850, easing: 'cubic-bezier(0.34, 1.4, 0.5, 1)' });

    if (refReflet.current) {
      refReflet.current.animate([
        { transform: 'translateX(0)' },
        { transform: 'translateX(750px)' }
      ], { duration: 1100, delay: 700, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' });
    }
  }, [ouvert]);

  if (!carte) return null;

  const ligneBasse = [
    carte.position ? carte.position.toUpperCase() : null,
    carte.equipe ? carte.equipe.trim().toUpperCase() : null,
    `${carte.nb_installations || 0} INSTALLATION${(carte.nb_installations || 0) > 1 ? 'S' : ''}`
  ].filter(Boolean).join(' · ');

  return (
    <>
      <button onClick={ouvrir}
        style={{
          width: '100%', padding: '14px', border: '1px solid #E5E7EB', borderRadius: '12px',
          background: '#fff', color: '#374151', fontSize: '15px', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '9px'
        }}>
        <CreditCard size={17} color="#10B981" /> Ma carte professionnelle
      </button>

      {ouvert && (
        <div onClick={() => setOuvert(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)',
            backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', zIndex: 120,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
          }}>
          <X size={26} color="#fff" style={{ position: 'absolute', top: '20px', right: '20px', cursor: 'pointer' }}
            onClick={() => setOuvert(false)} />

          <div ref={refCarte} onClick={(e) => e.stopPropagation()}
            style={{
              position: 'relative', width: '100%', maxWidth: '340px', aspectRatio: '1.586',
              borderRadius: '16px', overflow: 'hidden', padding: '18px 20px',
              display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
              background: 'linear-gradient(135deg, #00C97A 0%, #00A86B 35%, #04895C 70%, #0B6B4F 100%)',
              boxShadow: '0 12px 40px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.28)'
            }}>
            <div style={{ position: 'absolute', top: '-60px', left: '-30px', width: '220px', height: '160px', background: 'radial-gradient(ellipse at center, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0) 65%)', transform: 'rotate(-18deg)' }} />
            <div style={{ position: 'absolute', bottom: '-70px', right: '-50px', width: '200px', height: '170px', background: 'radial-gradient(ellipse at center, rgba(180,255,225,0.20) 0%, rgba(255,255,255,0) 70%)' }} />
            <div ref={refReflet} style={{ position: 'absolute', top: 0, left: '-45%', width: '45%', height: '100%', background: 'linear-gradient(100deg, rgba(255,255,255,0) 20%, rgba(255,255,255,0.35) 50%, rgba(255,255,255,0) 80%)' }} />

            <div style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px', background: 'rgba(255,255,255,0.18)', padding: '4px 10px', borderRadius: '999px' }}>
                <Star size={12} color="#fff" />
                <span style={{ fontSize: '12px', color: '#fff', fontWeight: 500 }}>
                  {carte.note_moyenne != null ? String(carte.note_moyenne).replace('.', ',') : '—'}
                </span>
              </div>
              <img src="/logo-eco-enr-blanc.png" alt="Eco'enr"
                style={{ height: '26px', width: 'auto', flexShrink: 0, opacity: 0.95 }} />
            </div>

            <div style={{ position: 'relative' }}>
              <div style={{ fontSize: '19px', fontWeight: 500, color: '#fff', letterSpacing: '0.4px', marginBottom: '5px' }}>
                {`${carte.prenom || ''} ${carte.nom || ''}`.toUpperCase()}
              </div>
              <div style={{ fontSize: '10.5px', color: 'rgba(255,255,255,0.88)', letterSpacing: '0.9px', marginBottom: '14px' }}>
                {(carte.poste || '').toUpperCase()} - PHOTOVOLTAÏQUE
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '12px' }}>
                <div style={{ minWidth: 0 }}>
                  {ligneBasse && (
                    <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.8)', letterSpacing: '0.6px', marginBottom: '3px' }}>
                      {ligneBasse}
                    </div>
                  )}
                  <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.72)', letterSpacing: '0.6px' }}>
                    N° {carte.numero_technicien || '123456'}
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CarteProfessionnelle;
